import React from 'react';
import {
  Box, Grid, Button, FormControl, InputLabel, Select, MenuItem,
  TextField, Typography, Paper, Switch, FormControlLabel,
  Autocomplete, Chip, Stack, CircularProgress, InputAdornment,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlaylistAddCheckOutlinedIcon from '@mui/icons-material/PlaylistAddCheckOutlined';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import { getRequest, postRequest } from 'Includes/api/apicall';
import { GET_URL, POST_URL } from 'Includes/urls';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';

const FILTER_PAPER_SX = {
  mt: 3,
  p: 2.5,
  borderRadius: 3,
  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
  border: '1px solid',
  borderColor: 'divider',
};

const TABLE_PAPER_SX = {
  mt: 3,
  p: 1.5,
  borderRadius: 3,
  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
  border: '1px solid',
  borderColor: 'divider',
  overflow: 'hidden',
};

const TEAM_STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'Active', label: 'Active' },
  { value: 'Low Engagement', label: 'Low Engagement' },
  { value: 'At Risk', label: 'At Risk' },
  { value: 'Dead', label: 'Dead' },
];

const ORDER_STATUS_OPTIONS = [
  { value: 'not_created', label: 'Order not created' },
  { value: 'created', label: 'Order created' },
  { value: '', label: 'All' },
];

function formatAmount(v) {
  const n = parseFloat(v || 0);
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function todayISO() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

function plusOneYearISO(dateStr) {
  const d = new Date(dateStr || todayISO());
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
}

function termPercents(count) {
  const n = Math.max(1, Number(count) || 2);
  const base = Math.floor((10000 / n)) / 100;
  const parts = Array(n).fill(base);
  const sum = parts.reduce((a, b) => a + b, 0);
  parts[n - 1] = Math.round((100 - (sum - base)) * 100) / 100;
  return parts;
}

function defaultRowSettings() {
  return { amount: '', withGst: true, termsCount: 2, studentCount: '', amountFixed: false };
}

function candidateToRow(r) {
  return {
    id: `company:${r.company}`,
    owner_key: r.owner_key || `company:${r.company}`,
    ownername: r.ownername || '',
    phonenumber: r.phonenumber || '',
    owneremail: r.owneremail || '',
    companies: [r],
    company: r.company,
    company_ids: [r.company],
    company_name: r.company_name,
    company_names: [r.company_name],
    institute_count: 1,
    uncovered_count: r.order_created ? 0 : 1,
    order_created: Boolean(r.order_created),
    merged: false,
    student_count: Number(r.student_count) || 0,
    report_student_count: Number(r.report_student_count) || 0,
    status_by_team: r.status_by_team,
    coordinator: r.coordinator,
    coordinator_name: r.coordinator_name,
    amount_per_student: r.amount_per_student,
    amount_fixed: Boolean(r.amount_fixed),
    order_base_amount: r.order_base_amount,
    order_gst_amount: r.order_gst_amount,
    order_total_amount: r.order_total_amount,
    service_gst_percentage: r.service_gst_percentage,
    sync_tab: r.sync_tab,
  };
}

function buildGroupRow(groupId, companyRows) {
  const companies = [...(companyRows || [])].sort((a, b) =>
    String(a.company_name || '').localeCompare(String(b.company_name || ''))
  );
  if (!companies.length) return null;
  const uncovered = companies.filter((c) => !c.order_created);
  const allCreated = uncovered.length === 0;
  const createdRow = companies.find((c) => c.order_created);
  // Prefer an institute that already has an order as primary when consolidating.
  const primary = createdRow || uncovered[0] || companies[0];
  const withCoord = companies.find((c) => c.coordinator) || primary;
  const baseTotal = companies.reduce((sum, c) => {
    const stored = parseFloat(c.order_base_amount);
    if (!Number.isNaN(stored) && stored > 0) return sum + stored;
    return sum + (Number(c.student_count) || 0) * (parseFloat(c.amount_per_student) || 0);
  }, 0);
  return {
    id: groupId,
    owner_key: primary.owner_key || `company:${primary.company}`,
    ownername: companies.find((c) => c.ownername)?.ownername || '',
    phonenumber: companies.find((c) => c.phonenumber)?.phonenumber || '',
    owneremail: companies.find((c) => c.owneremail)?.owneremail || '',
    companies,
    company: primary.company,
    company_ids: companies.map((c) => c.company),
    company_name: primary.company_name,
    company_names: companies.map((c) => c.company_name),
    institute_count: companies.length,
    uncovered_count: uncovered.length,
    order_created: allCreated,
    has_existing_orders: companies.some((c) => c.order_created),
    merged: true,
    student_count: companies.reduce((sum, c) => sum + (Number(c.student_count) || 0), 0),
    report_student_count: companies.reduce((sum, c) => sum + (Number(c.report_student_count) || 0), 0),
    status_by_team: primary.status_by_team,
    coordinator: withCoord?.coordinator,
    coordinator_name: withCoord?.coordinator_name,
    amount_per_student: (allCreated ? createdRow : primary)?.amount_per_student,
    amount_fixed: Boolean((allCreated ? createdRow : primary)?.amount_fixed),
    order_base_amount: allCreated && baseTotal > 0 ? String(baseTotal) : createdRow?.order_base_amount,
    order_gst_amount: createdRow?.order_gst_amount,
    order_total_amount: createdRow?.order_total_amount,
    service_gst_percentage: createdRow?.service_gst_percentage,
    sync_tab: primary.sync_tab,
  };
}

function applyManualMerges(data, mergeGroups) {
  const byCompany = new Map((data || []).map((r) => [r.company, r]));
  const used = new Set();
  const mergedRows = [];
  (mergeGroups || []).forEach((g) => {
    const members = (g.companyIds || []).map((id) => byCompany.get(id)).filter(Boolean);
    if (members.length < 2) return;
    members.forEach((m) => used.add(m.company));
    const row = buildGroupRow(g.id, members);
    if (row) mergedRows.push(row);
  });
  const singles = (data || []).filter((r) => !used.has(r.company)).map(candidateToRow);
  return [...mergedRows, ...singles];
}

function preventNumberScrollChange(e) {
  e.preventDefault();
  e.currentTarget.blur();
}

const AmountPerStudentCell = React.memo(function AmountPerStudentCell({
  companyId,
  disabled,
  amount,
  amountFixed,
  onAmountChange,
}) {
  return (
    <TextField
      key={`amount-${companyId}`}
      size="small"
      type="number"
      disabled={disabled}
      value={amount}
      onChange={(e) => onAmountChange(companyId, e.target.value)}
      inputProps={{
        min: 0,
        step: '0.01',
        onWheel: preventNumberScrollChange,
      }}
      InputProps={{
        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
      }}
      helperText={amountFixed ? 'Fixed amount + GST' : 'Per student × count + GST'}
      FormHelperTextProps={{ sx: { m: 0, whiteSpace: 'nowrap' } }}
      sx={{ width: '100%' }}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    />
  );
});

const StudentCountCell = React.memo(function StudentCountCell({
  companyId,
  disabled,
  studentCount,
  onStudentCountChange,
}) {
  return (
    <TextField
      key={`students-${companyId}`}
      size="small"
      type="number"
      disabled={disabled}
      value={studentCount}
      onChange={(e) => onStudentCountChange(companyId, e.target.value)}
      inputProps={{
        min: 0,
        step: 1,
        onWheel: preventNumberScrollChange,
      }}
      sx={{ width: '100%' }}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    />
  );
});

const AmountFixedCell = React.memo(function AmountFixedCell({
  companyId,
  disabled,
  amountFixed,
  onAmountFixedChange,
}) {
  return (
    <FormControlLabel
      key={`fixed-${companyId}`}
      sx={{ m: 0 }}
      control={
        <Switch
          size="small"
          checked={Boolean(amountFixed)}
          disabled={disabled}
          onChange={(e) => onAmountFixedChange(companyId, e.target.checked)}
          onClick={(e) => e.stopPropagation()}
        />
      }
      label={
        <Typography variant="caption">
          {amountFixed ? 'On' : 'Off'}
        </Typography>
      }
    />
  );
});

const GstSwitchCell = React.memo(function GstSwitchCell({
  companyId,
  disabled,
  withGst,
  onGstChange,
}) {
  return (
    <FormControlLabel
      key={`gst-${companyId}`}
      sx={{ m: 0 }}
      control={
        <Switch
          size="small"
          checked={Boolean(withGst)}
          disabled={disabled}
          onChange={(e) => onGstChange(companyId, e.target.checked)}
          onClick={(e) => e.stopPropagation()}
        />
      }
      label={
        <Typography variant="caption">
          {withGst ? 'On' : 'Off'}
        </Typography>
      }
    />
  );
});

const TermsCountCell = React.memo(function TermsCountCell({
  companyId,
  disabled,
  termsCount,
  onTermsChange,
}) {
  const percents = termPercents(termsCount);
  return (
    <Box
      key={`terms-${companyId}`}
      sx={{ width: '100%' }}
      onClick={(e) => e.stopPropagation()}
    >
      <TextField
        size="small"
        type="number"
        disabled={disabled}
        value={termsCount}
        onChange={(e) => {
          const v = Math.min(12, Math.max(1, parseInt(e.target.value || '2', 10) || 2));
          onTermsChange(companyId, v);
        }}
        inputProps={{
          min: 1,
          max: 12,
          onWheel: preventNumberScrollChange,
        }}
        sx={{ width: '100%' }}
        onKeyDown={(e) => e.stopPropagation()}
      />
      <Typography variant="caption" color="text.secondary" noWrap display="block">
        {percents.map((p) => `${p}%`).join(' / ')}
      </Typography>
    </Box>
  );
});

function BulkCreateOrders(props) {
  const navigate = useNavigate();

  const [academicYearList, setAcademicYearList] = React.useState([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = React.useState('');
  const [orderStatus, setOrderStatus] = React.useState('not_created');
  const [statusByTeam, setStatusByTeam] = React.useState('Active');
  const [search, setSearch] = React.useState('');

  const [orderDate, setOrderDate] = React.useState(todayISO());
  const [serviceEndDate, setServiceEndDate] = React.useState(plusOneYearISO(todayISO()));
  const [gstPercent, setGstPercent] = React.useState(18);

  const [users, setUsers] = React.useState([]);
  const [defaultCoordinator, setDefaultCoordinator] = React.useState(null);

  const [candidateRows, setCandidateRows] = React.useState([]);
  const [mergeGroups, setMergeGroups] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState([]);
  // rowId -> { amount, withGst, termsCount }
  const [rowSettings, setRowSettings] = React.useState({});
  const [bulkAmount, setBulkAmount] = React.useState('');
  const ignoreNextEmptySelection = React.useRef(false);
  const mergeSeq = React.useRef(1);

  const rows = React.useMemo(
    () => applyManualMerges(candidateRows, mergeGroups),
    [candidateRows, mergeGroups]
  );

  React.useEffect(() => {
    getRequest(GET_URL.academicyear.api, {}, props).then((res) => {
      if (res?.status === 200) {
        setAcademicYearList(res.data?.data || []);
      }
    });
    getRequest(GET_URL.user.api, { limit: 1000, pageno: 1 }, props).then((res) => {
      if (res?.status === 200) {
        const list = res.data?.data || res.data || [];
        setUsers(Array.isArray(list) ? list : []);
      }
    });
    getRequest(GET_URL.plans.api, {}, props).then((res) => {
      if (res?.status === 200) {
        const plans = res.data?.data || res.data || [];
        const erp = (Array.isArray(plans) ? plans : []).find((p) => {
          const blob = `${p?.service_name || ''} ${p?.name || ''}`.toLowerCase();
          return blob.includes('erp') && !blob.includes('digital');
        }) || plans?.[0];
        if (erp?.default_tax_amount != null) {
          setGstPercent(Number(erp.default_tax_amount) || 18);
        }
      }
    }).catch(() => {});
  }, []);

  const getSettings = React.useCallback(
    (companyId) => rowSettings[companyId] || defaultRowSettings(),
    [rowSettings]
  );

  const patchSettings = React.useCallback((companyId, patch) => {
    setRowSettings((prev) => ({
      ...prev,
      [companyId]: { ...defaultRowSettings(), ...(prev[companyId] || {}), ...patch },
    }));
  }, []);

  const handleAmountChange = React.useCallback(
    (companyId, value) => patchSettings(companyId, { amount: value }),
    [patchSettings]
  );

  const handleGstChange = React.useCallback(
    (companyId, withGst) => patchSettings(companyId, { withGst }),
    [patchSettings]
  );

  const handleTermsChange = React.useCallback(
    (companyId, termsCount) => patchSettings(companyId, { termsCount }),
    [patchSettings]
  );

  const handleStudentCountChange = React.useCallback(
    (companyId, studentCount) => patchSettings(companyId, { studentCount }),
    [patchSettings]
  );

  const handleAmountFixedChange = React.useCallback(
    (companyId, amountFixed) => patchSettings(companyId, { amountFixed }),
    [patchSettings]
  );

  const loadCandidates = React.useCallback((year) => {
    if (!year) {
      setCandidateRows([]);
      setMergeGroups([]);
      setSelectedIds([]);
      return;
    }
    setLoading(true);
    const params = {
      bulk_order_candidates: 1,
      year,
      ...(orderStatus ? { order_status: orderStatus } : {}),
      ...(statusByTeam ? { status_by_team: statusByTeam } : {}),
      ...(search.trim() ? { search: search.trim() } : {}),
    };
    getRequest(GET_URL.billing_config.api, params, props)
      .then((res) => {
        if (res?.status !== 200) return;
        const data = res.data?.data || [];
        const mappedIds = data.filter((r) => !r.order_created).map((r) => `company:${r.company}`);
        ignoreNextEmptySelection.current = true;
        setCandidateRows(data);
        setMergeGroups([]);
        setSelectedIds(mappedIds);
        setRowSettings((prev) => {
          const next = { ...prev };
          data.forEach((r) => {
            const rowId = `company:${r.company}`;
            const existing = next[rowId] || {};
            next[rowId] = {
              ...defaultRowSettings(),
              ...existing,
              studentCount: existing.studentCount !== undefined && existing.studentCount !== ''
                ? existing.studentCount
                : (r.student_count ? String(r.student_count) : ''),
              amount: existing.amount !== undefined && existing.amount !== ''
                ? existing.amount
                : (r.amount_per_student != null && r.amount_per_student !== ''
                  ? String(r.amount_per_student)
                  : ''),
              amountFixed: existing.amountFixed !== undefined
                ? existing.amountFixed
                : Boolean(r.amount_fixed),
            };
          });
          return next;
        });
      })
      .finally(() => setLoading(false));
  }, [orderStatus, statusByTeam, search, props]);

  React.useEffect(() => {
    if (selectedAcademicYear) {
      loadCandidates(selectedAcademicYear);
    }
  }, [selectedAcademicYear, orderStatus, statusByTeam]);

  const getStudentCount = (companyId, row) => {
    const settings = getSettings(companyId);
    if (settings.studentCount !== undefined && settings.studentCount !== '') {
      return parseFloat(settings.studentCount) || 0;
    }
    return parseFloat(row?.student_count || 0) || 0;
  };

  const calcBase = (companyId, row) => {
    if (row?.order_created) {
      const stored = parseFloat(row.order_base_amount);
      if (!Number.isNaN(stored) && stored >= 0) return stored;
    }
    const settings = getSettings(companyId);
    const amt = parseFloat(settings.amount || 0) || 0;
    if (settings.amountFixed) return amt;
    const students = getStudentCount(companyId, row);
    return amt * students;
  };

  const calcGst = (companyId, base, row) => {
    if (row?.order_created) {
      const stored = parseFloat(row.order_gst_amount);
      if (!Number.isNaN(stored) && stored >= 0) return stored;
    }
    if (!getSettings(companyId).withGst) return 0;
    return (base * (parseFloat(gstPercent) || 0)) / 100;
  };

  const applyAmountToSelected = (value) => {
    setRowSettings((prev) => {
      const next = { ...prev };
      selectedIds.forEach((id) => {
        next[id] = { ...defaultRowSettings(), ...(next[id] || {}), amount: value };
      });
      return next;
    });
  };

  const handleMergeSelected = () => {
    const selectedRows = rows.filter((r) => selectedIds.includes(r.id));
    const companyIds = [];
    selectedRows.forEach((r) => {
      (r.company_ids || []).forEach((id) => {
        if (!companyIds.includes(id)) companyIds.push(id);
      });
    });
    if (companyIds.length < 2) {
      Swal.fire(
        'Select institutes',
        'Select at least 2 institutes to merge into the same bill.',
        'warning'
      );
      return;
    }
    const mergeId = `merge:${mergeSeq.current}`;
    mergeSeq.current += 1;
    const studentCount = selectedRows.reduce((sum, r) => sum + getStudentCount(r.id, r), 0);
    const firstSettings = getSettings(selectedRows[0].id);
    setMergeGroups((prev) => {
      const remaining = prev.filter((g) => !g.companyIds.some((id) => companyIds.includes(id)));
      return [...remaining, { id: mergeId, companyIds }];
    });
    setRowSettings((prev) => ({
      ...prev,
      [mergeId]: {
        ...defaultRowSettings(),
        ...firstSettings,
        studentCount: studentCount ? String(studentCount) : '',
      },
    }));
    ignoreNextEmptySelection.current = true;
    setSelectedIds([mergeId]);
  };

  const handleUnmerge = React.useCallback((row) => {
    if (!row?.merged) return;
    const companyRowIds = (row.company_ids || []).map((id) => `company:${id}`);
    setMergeGroups((prev) => prev.filter((g) => g.id !== row.id));
    ignoreNextEmptySelection.current = true;
    setSelectedIds(companyRowIds);
  }, []);

  const mergeableInstituteCount = React.useMemo(
    () => rows
      .filter((r) => selectedIds.includes(r.id))
      .reduce((sum, r) => sum + (r.company_ids?.length || 1), 0),
    [rows, selectedIds]
  );

  const columns = React.useMemo(() => [
    {
      field: 'company_name',
      headerName: 'Institute',
      flex: 1.4,
      minWidth: 220,
      renderCell: (params) => (
        <Box key={`company-${params.row.id}`} sx={{ minWidth: 0, py: 0.5 }}>
          {params.row.merged ? (
            <>
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.25 }}>
                <Chip label="Same bill" size="small" color="primary" />
                <Typography variant="caption" color="text.secondary">
                  {params.row.institute_count} institutes
                </Typography>
              </Stack>
              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                {(params.row.companies || []).slice(0, 3).map((c) => (
                  <Chip key={`${params.row.id}-${c.company}`} label={c.company_name} size="small" variant="outlined" />
                ))}
                {(params.row.companies || []).length > 3 ? (
                  <Chip
                    label={`+${params.row.companies.length - 3}`}
                    size="small"
                    variant="outlined"
                    title={(params.row.company_names || []).slice(3).join(', ')}
                  />
                ) : null}
              </Stack>
            </>
          ) : (
            <Typography variant="body2" fontWeight={600} noWrap title={params.value}>
              {params.value || '-'}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            ERP · {params.row.status_by_team || 'Active'}
            {params.row.order_created ? ' · Order created' : ''}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'ownername',
      headerName: 'Owner',
      flex: 0.9,
      minWidth: 140,
      renderCell: (params) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" noWrap title={params.row.ownername}>
            {params.row.ownername || '-'}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {params.row.phonenumber || params.row.owneremail || ''}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'report_student_count',
      headerName: 'Report count',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip
          key={`report-count-${params.row.id}`}
          label={params.row.report_student_count ?? params.row.student_count ?? 0}
          size="small"
          color={Number(params.row.report_student_count) > 0 ? 'primary' : 'default'}
          variant="outlined"
          title={params.row.sync_tab ? `Client report: ${params.row.sync_tab}` : 'No client report'}
        />
      ),
    },
    {
      field: 'student_count',
      headerName: 'Student count',
      width: 130,
      sortable: false,
      renderCell: (params) => {
        const disabled = Boolean(params.row.order_created) && !params.row.merged;
        const settings = getSettings(params.row.id);
        const value = settings.studentCount !== undefined && settings.studentCount !== ''
          ? settings.studentCount
          : String(params.row.student_count ?? '');
        return (
          <StudentCountCell
            companyId={params.row.id}
            disabled={disabled}
            studentCount={value}
            onStudentCountChange={handleStudentCountChange}
          />
        );
      },
    },
    {
      field: 'amount_fixed',
      headerName: 'Amount fixed',
      width: 120,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const disabled = Boolean(params.row.order_created) && !params.row.merged;
        const settings = getSettings(params.row.id);
        return (
          <AmountFixedCell
            companyId={params.row.id}
            disabled={disabled}
            amountFixed={settings.amountFixed}
            onAmountFixedChange={handleAmountFixedChange}
          />
        );
      },
    },
    {
      field: 'amount_per_student',
      headerName: 'Amount',
      width: 160,
      sortable: false,
      renderCell: (params) => {
        const disabled = Boolean(params.row.order_created) && !params.row.merged;
        const settings = getSettings(params.row.id);
        return (
          <AmountPerStudentCell
            companyId={params.row.id}
            disabled={disabled}
            amount={settings.amount}
            amountFixed={settings.amountFixed}
            onAmountChange={handleAmountChange}
          />
        );
      },
    },
    {
      field: 'with_gst',
      headerName: 'GST',
      width: 110,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const disabled = Boolean(params.row.order_created) && !params.row.merged;
        const settings = getSettings(params.row.id);
        return (
          <GstSwitchCell
            companyId={params.row.id}
            disabled={disabled}
            withGst={settings.withGst}
            onGstChange={handleGstChange}
          />
        );
      },
    },
    {
      field: 'terms_count',
      headerName: 'Terms',
      width: 130,
      sortable: false,
      renderCell: (params) => {
        const disabled = Boolean(params.row.order_created) && !params.row.merged;
        const settings = getSettings(params.row.id);
        return (
          <TermsCountCell
            companyId={params.row.id}
            disabled={disabled}
            termsCount={settings.termsCount}
            onTermsChange={handleTermsChange}
          />
        );
      },
    },
    {
      field: 'base_amount',
      headerName: 'Amount',
      width: 120,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Typography
          key={`base-${params.row.id}`}
          variant="body2"
          fontWeight={600}
          sx={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {formatAmount(calcBase(params.row.id, params.row))}
        </Typography>
      ),
    },
    {
      field: 'gst_amount',
      headerName: `GST amt`,
      width: 110,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => {
        const base = calcBase(params.row.id, params.row);
        return (
          <Typography
            key={`gst-amt-${params.row.id}`}
            variant="body2"
            sx={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {formatAmount(calcGst(params.row.id, base, params.row))}
          </Typography>
        );
      },
    },
    {
      field: 'total_amount',
      headerName: 'Total',
      width: 120,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => {
        const base = calcBase(params.row.id, params.row);
        const total = params.row.order_created && params.row.order_total_amount != null
          ? (parseFloat(params.row.order_total_amount) || 0)
          : base + calcGst(params.row.id, base, params.row);
        return (
          <Typography
            key={`total-${params.row.id}`}
            variant="body2"
            fontWeight={700}
            sx={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {formatAmount(total)}
          </Typography>
        );
      },
    },
    {
      field: 'merge_actions',
      headerName: '',
      width: 110,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        params.row.merged ? (
          <Button
            size="small"
            startIcon={<CallSplitIcon />}
            onClick={(e) => {
              e.stopPropagation();
              handleUnmerge(params.row);
            }}
            sx={{ textTransform: 'none' }}
          >
            Unmerge
          </Button>
        ) : null
      ),
    },
  ], [getSettings, handleAmountChange, handleGstChange, handleTermsChange, handleStudentCountChange, handleAmountFixedChange, handleUnmerge, rowSettings, gstPercent]);

  const selectedSummary = React.useMemo(() => {
    let base = 0;
    let gst = 0;
    let count = 0;
    const isCreatedFilter = ['created', 'order_created'].includes(
      String(orderStatus || '').toLowerCase()
    );
    rows.forEach((r) => {
      if (isCreatedFilter) {
        if (!selectedIds.includes(r.id)) return;
      } else if (!selectedIds.includes(r.id)) {
        return;
      } else if (r.order_created && !(r.merged && (r.company_ids || []).length > 1)) {
        return;
      }
      const b = calcBase(r.id, r);
      const g = calcGst(r.id, b, r);
      if (b > 0 || g > 0 || r.order_created || r.merged) {
        base += b;
        gst += g;
        count += 1;
      }
    });
    return { count, base, gst, total: base + gst };
  }, [rows, selectedIds, rowSettings, gstPercent, orderStatus]);

  const handleSubmit = () => {
    if (!selectedAcademicYear) {
      Swal.fire('Select year', 'Please select an academic year first', 'warning');
      return;
    }
    const items = [];
    for (const row of rows) {
      if (!selectedIds.includes(row.id)) continue;
      const isMergeExisting = Boolean(row.merged && (row.company_ids || []).length > 1);
      // Single institute with an order already: nothing to create.
      if (row.order_created && !isMergeExisting) continue;
      const settings = getSettings(row.id);
      let amt = parseFloat(settings.amount || 0);
      if ((!amt || amt <= 0) && row.amount_per_student != null && row.amount_per_student !== '') {
        amt = parseFloat(row.amount_per_student) || 0;
      }
      if ((!amt || amt <= 0) && row.order_base_amount != null) {
        amt = parseFloat(row.order_base_amount) || 0;
      }
      const students = getStudentCount(row.id, row);
      const amountFixed = Boolean(settings.amountFixed) || (
        isMergeExisting && row.order_created && Boolean(row.amount_fixed)
      ) || (
        isMergeExisting && row.order_created && !(settings.amount && parseFloat(settings.amount) > 0)
        && parseFloat(row.order_base_amount) > 0
      );
      if (!amt || amt <= 0) {
        Swal.fire(
          'Amount required',
          amountFixed
            ? `Enter amount for ${row.ownername || row.company_name}`
            : `Enter amount per student for ${row.ownername || row.company_name}`,
          'warning'
        );
        return;
      }
      if (!amountFixed && (!students || students <= 0)) {
        Swal.fire('Student count', `Enter student count for ${row.ownername || row.company_name}`, 'warning');
        return;
      }
      items.push({
        company: row.company_ids[0],
        companies: row.company_ids,
        amount_per_student: amt,
        student_count: students,
        amount_fixed: amountFixed,
        coordinator: row.coordinator || defaultCoordinator?.id || null,
        with_gst: Boolean(settings.withGst),
        terms_count: Number(settings.termsCount) || 2,
        gst_percentage: settings.withGst ? gstPercent : 0,
        merge_existing: isMergeExisting && Boolean(row.has_existing_orders || row.order_created),
      });
    }
    if (!items.length) {
      Swal.fire(
        'Select companies',
        'Select institutes to create orders, or merge institutes (including those with existing orders) for the same bill.',
        'warning'
      );
      return;
    }
    const needsCoord = items.some((i) => !i.coordinator && !i.merge_existing);
    if (needsCoord && !defaultCoordinator?.id) {
      Swal.fire('Coordinator required', 'Select a default coordinator for companies without one', 'warning');
      return;
    }

    const gstOn = items.filter((i) => i.with_gst).length;
    const instituteCount = items.reduce((sum, i) => sum + (i.companies?.length || 1), 0);
    const mergeCount = items.filter((i) => (i.companies?.length || 1) > 1).length;
    Swal.fire({
      title: mergeCount ? 'Create / merge ERP orders?' : 'Create bulk ERP orders?',
      html: `Process <b>${items.length}</b> ERP order(s) covering <b>${instituteCount}</b> institute(s)<br/>Merged institutes share one order / bill (existing orders can be consolidated).<br/>GST on: ${gstOn} · GST off: ${items.length - gstOn}`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: mergeCount ? 'Create / Merge' : 'Create',
    }).then((result) => {
      if (!result.isConfirmed) return;
      setSubmitting(true);
      postRequest(
        `${POST_URL.billing_config.api}?bulk_create_orders=1`,
        {
          bulk_create_orders: 1,
          year: selectedAcademicYear,
          gst_percentage: gstPercent,
          order_date: orderDate,
          service_end_date: serviceEndDate,
          default_coordinator: defaultCoordinator?.id || null,
          items,
        },
        props
      )
        .then((res) => {
          if (res?.status === 200 || res?.status === 201) {
            const created = res.data?.created_count || 0;
            const merged = res.data?.merged_count || 0;
            const skipped = res.data?.skipped_count || 0;
            const parts = [];
            if (created) parts.push(`created ${created}`);
            if (merged) parts.push(`merged ${merged}`);
            if (skipped) parts.push(`skipped ${skipped}`);
            Swal.fire(
              'Done',
              parts.length ? parts.join(', ') : 'No changes',
              'success'
            ).then(() => {
              loadCandidates(selectedAcademicYear);
              setRowSettings({});
              setMergeGroups([]);
            });
          }
        })
        .finally(() => setSubmitting(false));
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Grid container alignItems="center" spacing={2}>
        <Grid item xs={12} md={7}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/billing/company-setup/list')}
              sx={{ textTransform: 'none' }}
            >
              Back
            </Button>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                Create Bulk ERP Orders
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ERP only. Select institutes (including those with existing orders) and use Merge for same bill to share one order.
              </Typography>
            </Box>
          </Stack>
        </Grid>
        <Grid item xs={12} md={5} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
          <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<CallMergeIcon />}
              disabled={submitting || mergeableInstituteCount < 2}
              onClick={handleMergeSelected}
              sx={{ borderRadius: 2, textTransform: 'none', px: 2.5 }}
            >
              Merge for same bill
            </Button>
            <Button
              variant="contained"
              startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <PlaylistAddCheckOutlinedIcon />}
              disabled={submitting || !selectedIds.length}
              onClick={handleSubmit}
              sx={{ borderRadius: 2, textTransform: 'none', px: 2.5 }}
            >
              Create Orders ({selectedSummary.count})
            </Button>
          </Stack>
        </Grid>
      </Grid>

      <Paper sx={FILTER_PAPER_SX}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Academic Year</InputLabel>
              <Select
                value={selectedAcademicYear}
                label="Academic Year"
                onChange={(e) => setSelectedAcademicYear(e.target.value)}
              >
                {academicYearList.map((y) => (
                  <MenuItem key={y.id} value={y.id}>{y.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Order status</InputLabel>
              <Select
                value={orderStatus}
                label="Order status"
                onChange={(e) => setOrderStatus(e.target.value)}
              >
                {ORDER_STATUS_OPTIONS.map((o) => (
                  <MenuItem key={o.label} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status by team</InputLabel>
              <Select
                value={statusByTeam}
                label="Status by team"
                onChange={(e) => setStatusByTeam(e.target.value)}
              >
                {TEAM_STATUS_OPTIONS.map((o) => (
                  <MenuItem key={o.label} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search company / owner"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') loadCandidates(selectedAcademicYear);
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Order date"
              InputLabelProps={{ shrink: true }}
              value={orderDate}
              onChange={(e) => {
                setOrderDate(e.target.value);
                setServiceEndDate(plusOneYearISO(e.target.value));
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="End date"
              InputLabelProps={{ shrink: true }}
              value={serviceEndDate}
              onChange={(e) => setServiceEndDate(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              options={users}
              getOptionLabel={(u) => u.username || u.email || String(u.id)}
              value={defaultCoordinator}
              onChange={(_e, v) => setDefaultCoordinator(v)}
              renderInput={(params) => (
                <TextField {...params} label="Default coordinator" size="small" />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={5}>
            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                type="number"
                label="Apply amount to selected"
                value={bulkAmount}
                onChange={(e) => setBulkAmount(e.target.value)}
                inputProps={{
                  onWheel: preventNumberScrollChange,
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                sx={{ flex: 1 }}
              />
              <Button
                variant="outlined"
                disabled={!selectedIds.length || bulkAmount === ''}
                onClick={() => applyAmountToSelected(bulkAmount)}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
              >
                Apply
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {selectedAcademicYear && (
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
              <Typography variant="caption" color="text.secondary">Selected base</Typography>
              <Typography variant="h6" fontWeight={600}>{formatAmount(selectedSummary.base)}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
              <Typography variant="caption" color="text.secondary">GST</Typography>
              <Typography variant="h6" fontWeight={600}>{formatAmount(selectedSummary.gst)}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>Total</Typography>
              <Typography variant="h6" fontWeight={700}>{formatAmount(selectedSummary.total)}</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      <Paper sx={TABLE_PAPER_SX}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          autoHeight
          rowHeight={92}
          getRowId={(row) => row.id}
          checkboxSelection
          isRowSelectable={() => true}
          selectionModel={selectedIds}
          onSelectionModelChange={(ids) => {
            if (ignoreNextEmptySelection.current) {
              ignoreNextEmptySelection.current = false;
              if (!ids.length) return;
            }
            setSelectedIds(ids);
          }}
          disableSelectionOnClick
          disableVirtualization
          pageSize={50}
          rowsPerPageOptions={[25, 50, 100]}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f5f7fb', fontWeight: 600 },
            '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
          }}
          components={{
            NoRowsOverlay: () => (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  {selectedAcademicYear ? 'No companies match these filters' : 'Select an academic year'}
                </Typography>
              </Box>
            ),
          }}
        />
      </Paper>
    </Box>
  );
}

export default BulkCreateOrders;
